import React, { useEffect, useState } from 'react'
import {
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CButton, CAlert, CSpinner, CBadge, CFormLabel, CFormSelect, CFormInput, CFormCheck,
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
} from '@coreui/react'
import { api } from '../api/client'
import { fmtMoneda } from '../utils/moneda'

// [RGM] 2026-10-14 (feature devoluciones Fase 1 — diseño aprobado:
// odd/tasks/devoluciones-facturacion.md): modal COMPARTIDO por la grilla de
// Facturas y la vista de Factura. Carga el detalle de la factura + el
// acumulado por línea (GET /facturas/{nro}/devoluciones) para calcular
// "cuánto queda por devolver", arma el resumen en vivo (Subtotal − Descuento
// proporcional + IVA sobre la base neta) y confirma con
// POST /facturas/{nro}/devolucion (backend ya implementado — solo rol admin /
// factura activa / crédito con préstamo vinculado / no devolución).

// Motivos del combo obligatorio (diseño Fase 1 regla 2: "Motivo obligatorio").
// El texto viaja tal cual al backend, que lo guarda en la observación del
// documento de devolución y de la factura original
// (FacturasController.cs:864/:931 — réplica del patrón de observaciones GX).
export const MOTIVOS_DEVOLUCION = ['Defectuoso', 'No era lo pedido', 'Error de facturación', 'Otro']

/** Línea del modal: acumulado (vendida/ya devuelta) + selección de la tanda. */
export interface LineaDevolucion {
  productoNro: number
  productoNombre: string
  vendida: number
  devuelta: number
  /** Máxima devolvible = vendida − ya devuelta (backend: CantidadDevolvible). */
  maxDevolvible: number
  valor: number
  marcada: boolean
  cantidad: string
}

/** Respuesta del POST /facturas/{nro}/devolucion (FacturasController.cs:952-969). */
export interface DevolucionCreada {
  devolucionNro: number
  facturaNroOriginal: number
  subTotalDevuelto: number
  descuentoDevuelto?: number
  ivaDevuelto: number
  totalDevuelto: number
  motivo: string
  autorizadoPor: string
  egresoNro?: number | null
  egresoValor?: number
  excedenteCredito?: number
  facturaOriginalEstado: number
  devolucionTotal: boolean
}

interface Props {
  /** Nro de la factura a devolver; null = modal cerrado. */
  facturaNro: number | null
  empresa: string
  onClose: () => void
  /** El padre cierra, refresca la grilla/vista y ofrece "Imprimir devolución". */
  onCreada: (devolucion: DevolucionCreada) => void
}

const DevolucionFacturaModal: React.FC<Props> = ({ facturaNro, empresa, onClose, onCreada }) => {
  const [factura, setFactura] = useState<any | null>(null)
  const [lineas, setLineas] = useState<LineaDevolucion[]>([])
  const [totalDevuelto, setTotalDevuelto] = useState(0)
  const [motivo, setMotivo] = useState('')
  const [cargando, setCargando] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  // Al abrir (o cambiar de factura): detalle + acumulado de devoluciones.
  // El acumulado manda para vendida/ya devuelta (mismo cálculo del backend,
  // AcumuladoDevolucionesAsync); el detalle aporta descripción y valor.
  useEffect(() => {
    if (facturaNro == null) return
    setCargando(true)
    setError('')
    setMotivo('')
    setFactura(null)
    setLineas([])
    setTotalDevuelto(0)
    Promise.all([
      api.get<any>(`/facturas/${facturaNro}?empresa=${empresa}`),
      api.get<any>(`/facturas/${facturaNro}/devoluciones?empresa=${empresa}`),
    ])
      .then(([detalle, acumulado]) => {
        setFactura(detalle)
        setTotalDevuelto(Number(acumulado?.totalDevuelto ?? 0))
        const porProducto = new Map<number, any>(
          (acumulado?.acumuladoPorLinea ?? []).map((a: any) => [Number(a.productoNro), a]),
        )
        setLineas((detalle?.lineas ?? []).map((l: any) => {
          const acc = porProducto.get(Number(l.productoNro))
          const vendida = Number(acc?.vendida ?? l.cantidad ?? 0)
          const devuelta = Number(acc?.devuelta ?? 0)
          const maxDevolvible = Math.max(0, Number(acc?.pendiente ?? (vendida - devuelta)))
          return {
            productoNro: Number(l.productoNro),
            productoNombre: String(l.productoNombre ?? ''),
            vendida,
            devuelta,
            maxDevolvible,
            valor: Number(l.valor ?? 0),
            // Default de la cantidad = máxima devolvible (diseño: "default = la
            // máxima devolvible"); la casilla arranca SIN marcar (AJUSTES
            // 2026-10-14 punto 3) — [Seleccionar todo] marca todas las
            // devolvibles al máximo, [Limpiar selección] las desmarca.
            marcada: false,
            cantidad: String(maxDevolvible),
          }
        }))
      })
      .catch((e: any) => setError(e?.message ?? 'No se pudo cargar la factura'))
      .finally(() => setCargando(false))
  }, [facturaNro, empresa])

  // Resumen financiero EN VIVO (solo líneas marcadas). Mismo cálculo del
  // backend (AJUSTES 2026-10-14 punto 1): Subtotal = Σ cantidad × ProductoValor;
  // el descuento de la factura se REVIERTE proporcionalmente
  // (DescuentoDev = DescuentoOrig × brutoDev/brutoOrig) y el IVA es
  // proporcional sobre la base NETA (SubTotal − Descuento) — mismas fórmulas de
  // ReglasFacturacion.CalcularDescuentoProporcionalDevolucion /
  // CalcularIvaProporcionalDevolucion (ReglasFacturacion.cs). El Total a
  // devolver es el valor EFECTIVO (neto + IVA): lo que aplica el backend.
  const seleccionadas = lineas.filter((l) => l.marcada && (Number(l.cantidad) || 0) > 0)
  const subTotalDevolver = seleccionadas.reduce((acc, l) => acc + l.valor * (Number(l.cantidad) || 0), 0)
  const subTotalOriginal = Number(factura?.subTotal ?? 0)
  // `descuento` del detalle = % de la factura original (FacturaLibreValor3).
  const descuentoPct = Number(factura?.descuento ?? 0)
  const descuentoOriginal = subTotalOriginal * (descuentoPct / 100)
  const descuentoDevolver = subTotalOriginal === 0
    ? 0 : descuentoOriginal * (subTotalDevolver / subTotalOriginal)
  const netoDevolver = subTotalDevolver - descuentoDevolver
  const netoOriginal = subTotalOriginal - descuentoOriginal
  const ivaDevolver = netoOriginal === 0
    ? 0 : Number(factura?.facturaIVA ?? 0) * (netoDevolver / netoOriginal)
  const totalDevolver = netoDevolver + ivaDevolver
  const esCredito = Number(factura?.facturaFPago) === 2

  const marcarTodo = () => setLineas((ls) => ls.map((l) => (
    l.maxDevolvible > 0 ? { ...l, marcada: true, cantidad: String(l.maxDevolvible) } : l
  )))
  const limpiarSeleccion = () => setLineas((ls) => ls.map((l) => ({
    ...l, marcada: false, cantidad: String(l.maxDevolvible),
  })))

  const cambioLinea = (i: number, cambio: Partial<LineaDevolucion>) => {
    setLineas((ls) => ls.map((l, j) => (j === i ? { ...l, ...cambio } : l)))
  }

  const confirmar = async () => {
    if (facturaNro == null) return
    setError('')
    const pedidas = lineas.filter((l) => l.marcada)
    // Mismos mensajes del backend (FacturasController.cs:722-727 / 767-772): se
    // validan también aquí para no gastar el viaje y señalar la línea exacta.
    if (pedidas.length === 0) { setError('La devolución debe tener al menos un producto'); return }
    if (pedidas.some((l) => (Number(l.cantidad) || 0) <= 0)) { setError('Cantidad Mayor a Cero'); return }
    const excedida = pedidas.find((l) => (Number(l.cantidad) || 0) > l.maxDevolvible)
    if (excedida) { setError(`Cantidad mayor a la disponible (producto ${excedida.productoNro})`); return }
    if (!motivo) { setError('Motivo'); return }
    setGuardando(true)
    try {
      const res = await api.post<DevolucionCreada>(`/facturas/${facturaNro}/devolucion?empresa=${empresa}`, {
        lineas: pedidas.map((l) => ({ productoNro: l.productoNro, cantidad: Number(l.cantidad) || 0 })),
        motivo,
      })
      onCreada(res)
    } catch (e: any) {
      // Errores del backend visibles en el modal (patrón existente de la página).
      setError(e?.message ?? 'No se pudo registrar la devolución')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <CModal visible={facturaNro != null} onClose={onClose} size="lg">
      <CModalHeader>
        <CModalTitle>Devolución de factura #{facturaNro ?? ''}</CModalTitle>
      </CModalHeader>
      <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        {error && <CAlert color="danger">{error}</CAlert>}
        {cargando ? (
          <div className="text-center py-4"><CSpinner color="primary" /></div>
        ) : factura && (
          <>
            <div className="text-body-secondary small mb-3">
              {factura.clienteNombre || '—'} · {String(factura.facturaFecha ?? '').slice(0, 10)}
              {totalDevuelto > 0 && <> · Total devuelto hasta ahora: {fmtMoneda(totalDevuelto)}</>}
            </div>

            {/* Atajos de la tanda (AJUSTES 2026-10-14 punto 3): el modal
                arranca SIN selección; [Seleccionar todo] marca todas las
                devolvibles al máximo y [Limpiar selección] desmarca todas. */}
            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
              <span className="fw-semibold">Productos a devolver</span>
              <div className="d-flex gap-2">
                <CButton size="sm" color="secondary" variant="outline" disabled={guardando} onClick={marcarTodo}>Seleccionar todo</CButton>
                <CButton size="sm" color="secondary" variant="outline" disabled={guardando} onClick={limpiarSeleccion}>Limpiar selección</CButton>
              </div>
            </div>

            <CTable small bordered align="middle" className="mb-3">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell style={{ width: 40 }} />
                  <CTableHeaderCell>Producto</CTableHeaderCell>
                  <CTableHeaderCell className="text-end" style={{ width: 80 }}>Vendida</CTableHeaderCell>
                  <CTableHeaderCell className="text-end" style={{ width: 90 }}>Ya devuelta</CTableHeaderCell>
                  <CTableHeaderCell className="text-end" style={{ width: 90 }}>Disponible</CTableHeaderCell>
                  <CTableHeaderCell className="text-end" style={{ width: 110 }}>Cantidad</CTableHeaderCell>
                  <CTableHeaderCell className="text-end" style={{ width: 110 }}>Valor</CTableHeaderCell>
                  <CTableHeaderCell className="text-end" style={{ width: 120 }}>Subtotal</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {lineas.map((l, i) => {
                  // Fila deshabilitada cuando ya está toda devuelta (diseño).
                  const sinDisponible = l.maxDevolvible <= 0
                  const cant = Number(l.cantidad) || 0
                  return (
                    <CTableRow key={l.productoNro}>
                      <CTableDataCell>
                        <CFormCheck
                          checked={l.marcada}
                          disabled={sinDisponible || guardando}
                          aria-label={`Devolver ${l.productoNombre || l.productoNro}`}
                          onChange={(e) => cambioLinea(i, { marcada: e.target.checked })}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <div>{l.productoNombre || '—'}</div>
                        <small className="text-body-secondary">Nro {l.productoNro}</small>
                        {sinDisponible && <CBadge color="secondary" className="ms-2">Toda devuelta</CBadge>}
                      </CTableDataCell>
                      <CTableDataCell className="text-end">{l.vendida}</CTableDataCell>
                      <CTableDataCell className="text-end">{l.devuelta}</CTableDataCell>
                      <CTableDataCell className="text-end">{l.maxDevolvible}</CTableDataCell>
                      <CTableDataCell className="text-end">
                        <CFormInput
                          type="number"
                          size="sm"
                          min={1}
                          max={l.maxDevolvible}
                          step={1}
                          value={l.cantidad}
                          disabled={sinDisponible || !l.marcada || guardando}
                          onChange={(e) => cambioLinea(i, { cantidad: e.target.value })}
                        />
                      </CTableDataCell>
                      <CTableDataCell className="text-end">{fmtMoneda(l.valor)}</CTableDataCell>
                      <CTableDataCell className="text-end">
                        {l.marcada && cant > 0 ? fmtMoneda(l.valor * cant) : '—'}
                      </CTableDataCell>
                    </CTableRow>
                  )
                })}
                {lineas.length === 0 && (
                  <CTableRow>
                    <CTableDataCell colSpan={8} className="text-center text-body-secondary">Sin productos</CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>

            <div className="mb-3">
              <CFormLabel>Motivo</CFormLabel>
              <CFormSelect value={motivo} disabled={guardando} onChange={(e) => setMotivo(e.target.value)}>
                <option value="">Elegir…</option>
                {MOTIVOS_DEVOLUCION.map((m) => <option key={m} value={m}>{m}</option>)}
              </CFormSelect>
            </div>

            {/* Resumen EN VIVO antes de confirmar + destino financiero según la
                factura (crédito → baja deuda del crédito vinculado; contado/
                tarjeta → reembolso por Egreso). Los números exactos los aplica
                el backend; aquí no se inventan montos financieros. */}
            <div className="border rounded p-3" style={{ background: 'rgba(7, 139, 205, 0.04)' }}>
              <div className="d-flex justify-content-between gap-4">
                <span className="text-body-secondary">Subtotal</span>
                <span className="fw-semibold">{fmtMoneda(subTotalDevolver)}</span>
              </div>
              {descuentoPct > 0 && (
                <div className="d-flex justify-content-between gap-4">
                  <span className="text-body-secondary">Descuento proporcional ({descuentoPct}%)</span>
                  <span className="fw-semibold">− {fmtMoneda(descuentoDevolver)}</span>
                </div>
              )}
              <div className="d-flex justify-content-between gap-4">
                <span className="text-body-secondary">IVA proporcional</span>
                <span className="fw-semibold">{fmtMoneda(ivaDevolver)}</span>
              </div>
              <div className="d-flex justify-content-between gap-4 border-top pt-2 mt-1">
                <span className="fw-bold">Total a devolver</span>
                <span className="fs-5 fw-bold">{fmtMoneda(totalDevolver)}</span>
              </div>
              <div className="small mt-2" style={{ color: '#078bcd' }}>
                {esCredito
                  ? 'Se bajará la deuda del crédito vinculado'
                  : 'Se generará reembolso por caja (egreso)'}
              </div>
            </div>
          </>
        )}
      </CModalBody>
      <CModalFooter className="border-top">
        <CButton color="secondary" type="button" disabled={guardando} onClick={onClose}>Cancelar</CButton>
        <CButton
          color="primary"
          type="button"
          disabled={guardando || cargando || !factura || seleccionadas.length === 0}
          onClick={confirmar}
        >
          {guardando ? <CSpinner size="sm" /> : 'Confirmar devolución'}
        </CButton>
      </CModalFooter>
    </CModal>
  )
}

export default DevolucionFacturaModal
